/**
 * v3.maintActions — Wave 6.3 close-out + Wave 10 RAM/RCM execution surface.
 *
 * Wave 6.3 (existing):
 *   POST /v3/maint-actions/{id}/resolve  body: ResolveMaintActionInput
 *   GET  /v3/maint-actions/my            (filtered by status)
 *
 * Wave 10 (new — A5 BLOCKER):
 *   POST /v3/maint/acct/change-title          body: ChangeAccountTitleInput
 *   POST /v3/maint/acct/change-ownership      body: ChangeAccountOwnershipTypeInput
 *   POST /v3/maint/acct/change-od-protection  body: ChangeOdProtectionInput
 *   POST /v3/maint/acct/change-od-limit       body: ChangeOdLimitInput
 *   POST /v3/maint/acct/change-statement-prefs body: ChangeStatementPrefsInput
 *   POST /v3/maint/acct/change-features       body: ChangeAccountFeaturesInput
 *   POST /v3/maint/acct/block-unblock         body: BlockUnblockAccountInput
 *   POST /v3/maint/acct/close                 body: CloseAccountInput
 *   POST /v3/maint/acct/reactivate-dormant    body: ReactivateDormantInput
 *   POST /v3/maint/acct/change-rate-index     body: ChangeRateIndexInput
 *   POST /v3/maint/cif/change-name            body: ChangeCifNameInput
 *   POST /v3/maint/cif/change-dob             body: ChangeCifDobInput
 *   POST /v3/maint/cif/change-tax-id          body: ChangeCifTaxIdInput
 *   POST /v3/maint/cif/change-contact         body: ChangeCifContactInput
 *   POST /v3/maint/cif/channel-prefs          body: ChangeChannelPreferencesInput
 *   POST /v3/maint/cif/employer-occupation    body: ChangeEmployerOccupationInput
 *   POST /v3/maint/cif/id-doc-refresh         body: IdDocumentRefreshInput
 *   POST /v3/maint/cif/foreign-status-update  body: ForeignStatusUpdateInput
 *   POST /v3/maint/cif/mark-deceased          body: MarkDeceasedInput
 *   POST /v3/maint/cif/ofac-rescreen          body: OfacRescreenInput
 *   POST /v3/maint/cross/joint-owner-add      body: JointOwnerAddInput
 *   POST /v3/maint/cross/joint-owner-remove   body: JointOwnerRemoveInput
 *   POST /v3/maint/cross/pod-beneficiary-add  body: AddPodBeneficiaryInput
 *
 * Account-scoped routes that lack a `cifId` body field rely on the
 * `x-pcb-cif` header (set by the api-client http layer when the FSR has
 * a focused CIF in context).
 */

import type {
  RequestOptions,
  ApiResult,
  MaintActionResolutionDto,
  ResolveMaintActionInput,
  ResolveMaintActionResponse,
  // Wave 10 inputs
  ChangeAccountTitleInput,
  ChangeAccountOwnershipTypeInput,
  ChangeOdProtectionInput,
  ChangeOdLimitInput,
  ChangeStatementPrefsInput,
  ChangeAccountFeaturesInput,
  BlockUnblockAccountInput,
  CloseAccountInput,
  ReactivateDormantInput,
  ChangeRateIndexInput,
  ChangeCifNameInput,
  ChangeCifDobInput,
  ChangeCifTaxIdInput,
  ChangeCifContactInput,
  ChangeChannelPreferencesInput,
  ChangeEmployerOccupationInput,
  IdDocumentRefreshInput,
  ForeignStatusUpdateInput,
  MarkDeceasedInput,
  OfacRescreenInput,
  JointOwnerAddInput,
  JointOwnerRemoveInput,
  AddPodBeneficiaryInput,
  MaintActionSubmitResponse,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface MaintActionListFilters {
  status?: 'open' | 'pending' | 'resolved' | 'all';
  branch?: string;
  officerCode?: string;
  top?: number;
}

export type SubmitEnvelope = ApiResult<MaintActionSubmitResponse>;

export class MaintActionsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public listMy(
    filters?: MaintActionListFilters,
    opts?: RequestOptions,
  ): Promise<ApiResult<MaintActionResolutionDto[]>> {
    return this.http.get<ApiResult<MaintActionResolutionDto[]>>('/v3/maint-actions/my', {
      ...opts,
      ...(filters
        ? { query: filters as unknown as Record<string, string | number | boolean> }
        : {}),
    });
  }

  public resolve(
    maintActionId: string,
    body: ResolveMaintActionInput,
    opts?: RequestOptions,
  ): Promise<ResolveMaintActionResponse> {
    return this.http.post<ResolveMaintActionResponse>(
      `/v3/maint-actions/${encodeURIComponent(maintActionId)}/resolve`,
      { ...opts, body },
    );
  }

  // ── Wave 10 — RAM (account-level) submit methods ─────────────────────────

  public submitChangeTitle(body: ChangeAccountTitleInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/acct/change-title', { ...opts, body });
  }
  public submitChangeOwnership(body: ChangeAccountOwnershipTypeInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/acct/change-ownership', { ...opts, body });
  }
  public submitChangeOdProtection(body: ChangeOdProtectionInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/acct/change-od-protection', { ...opts, body });
  }
  public submitChangeOdLimit(body: ChangeOdLimitInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/acct/change-od-limit', { ...opts, body });
  }
  public submitChangeStatementPrefs(body: ChangeStatementPrefsInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/acct/change-statement-prefs', { ...opts, body });
  }
  public submitChangeFeatures(body: ChangeAccountFeaturesInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/acct/change-features', { ...opts, body });
  }
  public submitBlockUnblock(body: BlockUnblockAccountInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/acct/block-unblock', { ...opts, body });
  }
  public submitClose(body: CloseAccountInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/acct/close', { ...opts, body });
  }
  public submitReactivateDormant(body: ReactivateDormantInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/acct/reactivate-dormant', { ...opts, body });
  }
  public submitChangeRateIndex(body: ChangeRateIndexInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/acct/change-rate-index', { ...opts, body });
  }

  // ── Wave 10 — RCM (customer-level) submit methods ────────────────────────

  public submitChangeName(body: ChangeCifNameInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cif/change-name', { ...opts, body });
  }
  public submitChangeDob(body: ChangeCifDobInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cif/change-dob', { ...opts, body });
  }
  public submitChangeTaxId(body: ChangeCifTaxIdInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cif/change-tax-id', { ...opts, body });
  }
  public submitChangeContact(body: ChangeCifContactInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cif/change-contact', { ...opts, body });
  }
  public submitChannelPrefs(body: ChangeChannelPreferencesInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cif/channel-prefs', { ...opts, body });
  }
  public submitEmployerOccupation(body: ChangeEmployerOccupationInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cif/employer-occupation', { ...opts, body });
  }
  public submitIdDocRefresh(body: IdDocumentRefreshInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cif/id-doc-refresh', { ...opts, body });
  }
  public submitForeignStatusUpdate(body: ForeignStatusUpdateInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cif/foreign-status-update', { ...opts, body });
  }
  public submitMarkDeceased(body: MarkDeceasedInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cif/mark-deceased', { ...opts, body });
  }
  public submitOfacRescreen(body: OfacRescreenInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cif/ofac-rescreen', { ...opts, body });
  }

  // ── Wave 10 — Cross-action submit methods ────────────────────────────────

  public submitJointOwnerAdd(body: JointOwnerAddInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cross/joint-owner-add', { ...opts, body });
  }
  public submitJointOwnerRemove(body: JointOwnerRemoveInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cross/joint-owner-remove', { ...opts, body });
  }
  public submitPodBeneficiaryAdd(body: AddPodBeneficiaryInput, opts?: RequestOptions): Promise<SubmitEnvelope> {
    return this.http.post<SubmitEnvelope>('/v3/maint/cross/pod-beneficiary-add', { ...opts, body });
  }
}
